<template>
  <div>
    <!-- Selected Keyword Banner -->
    <div v-if="selectedKeyword" class="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
      <div class="flex items-start justify-between gap-3">
        <div>
          <span class="text-xs text-primary-600 dark:text-primary-400 font-medium uppercase tracking-wide">Selected Keyword</span>
          <p class="text-sm font-semibold text-primary-800 dark:text-primary-200 mt-0.5">
            {{ selectedKeyword }}
          </p>
        </div>
        <button @click="beginEditSelectedKeyword" class="text-xs text-primary-600 dark:text-primary-400 hover:underline">
          Edit
        </button>
      </div>

      <form v-if="editingSelectedKeyword" class="mt-3 flex flex-col sm:flex-row gap-2" @submit.prevent="handleSelectedKeywordEdit">
        <input
          v-model="selectedKeywordDraft"
          type="text"
          class="flex-1 text-sm border border-primary-200 dark:border-primary-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          placeholder="Adjust selected keyword">
        <button
          type="submit"
          :disabled="selectedKeywordSaving || selectedKeywordDraft.trim().length === 0"
          class="btn btn-primary btn-sm">
          {{ selectedKeywordSaving ? 'Saving...' : 'Save Keyword' }}
        </button>
        <button type="button" class="btn btn-secondary btn-sm" @click="editingSelectedKeyword = false">
          Cancel
        </button>
      </form>
    </div>

    <!-- Header -->
    <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
      <h3 class="text-sm font-heading font-semibold text-gray-900 dark:text-gray-100">
        Keyword Research
      </h3>
      <div class="flex flex-col sm:flex-row sm:items-center gap-2">
        <input
          v-if="showSeedInput"
          v-model="seedKeyword"
          type="text"
          placeholder="Seed keyword (strongly recommended)"
          class="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-full sm:w-64"
          @keyup.enter="handleRunResearch">
        <button
          v-if="!showSeedInput"
          @click="showSeedInput = true"
          :disabled="running"
          class="text-xs text-gray-500 dark:text-gray-400 hover:underline">
          Set seed keyword
        </button>
        <button
          @click="handleRunResearch"
          :disabled="running"
          class="btn btn-primary btn-sm">
          {{ running ? stepMessage || 'Researching...' : (research ? 'Re-research' : 'Run Research') }}
        </button>
      </div>
    </div>

    <div v-if="pageTypeGuidance || seedSuggestions.length > 0" class="mb-4 space-y-3">
      <div v-if="pageTypeGuidance" class="p-3 border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 rounded-xl">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p class="text-xs font-semibold text-sky-800 dark:text-sky-200">
              {{ pageTypeGuidance.page_type }} keyword direction
            </p>
            <p class="text-xs text-sky-700 dark:text-sky-300 mt-1">
              {{ pageTypeGuidance.recommended_keyword_type }}
            </p>
          </div>
          <span class="inline-flex items-center px-2 py-0.5 rounded-lg text-xs bg-white dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-800">
            Strongly recommended
          </span>
        </div>
        <p class="text-xs text-sky-700 dark:text-sky-300 mt-2 leading-relaxed">
          {{ pageTypeGuidance.message }}
        </p>
      </div>

      <div v-if="seedSuggestions.length > 0" class="p-3 border border-gray-200 dark:border-gray-700 rounded-xl">
        <div class="flex items-center justify-between gap-2 mb-2">
          <p class="text-xs font-semibold text-gray-700 dark:text-gray-300">
            Suggested seed keywords
          </p>
          <span v-if="recommendedSeed" class="text-[11px] text-gray-500 dark:text-gray-400">
            Recommended: {{ recommendedSeed.keyword }}
          </span>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="suggestion in seedSuggestions"
            :key="suggestion.keyword"
            type="button"
            class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs transition-colors"
            :class="suggestion.recommended ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-primary-300 dark:hover:border-primary-700'"
            @click="useSeedSuggestion(suggestion)">
            <span>{{ suggestion.keyword }}</span>
            <span v-if="suggestion.recommended" class="font-semibold">Recommended</span>
          </button>
        </div>
        <p v-if="seedImpactMessage" class="text-xs text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
          {{ seedImpactMessage }}
        </p>
        <div v-if="seedValidation" class="mt-2 flex flex-wrap gap-2">
          <span class="inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
            {{ seedValidation.quality_label }}
          </span>
          <span class="inline-flex items-center px-2 py-0.5 rounded-lg text-[11px]"
            :class="seedValidation.local_intent ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'">
            {{ seedValidation.local_intent ? 'Local intent' : 'Local intent weak' }}
          </span>
          <span class="inline-flex items-center px-2 py-0.5 rounded-lg text-[11px]"
            :class="seedValidation.page_type_match ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'">
            {{ seedValidation.page_type_match ? 'Page intent match' : 'Page intent needs review' }}
          </span>
          <span class="inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
            Demand checked after research
          </span>
        </div>
      </div>
    </div>

    <div v-if="metricsNotice" class="mb-4 p-3 rounded-xl border text-xs"
      :class="metricsNoticeClass">
      {{ metricsNotice }}
    </div>

    <form class="mb-4 p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50" @submit.prevent="handleManualKeywordSelect">
      <label for="manual-keyword" class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
        Custom primary keyword
      </label>
      <div class="flex flex-col sm:flex-row gap-2">
        <input
          id="manual-keyword"
          v-model="manualKeyword"
          type="text"
          placeholder="Type a keyword to use as primary"
          class="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <button
          type="submit"
          :disabled="manualSelecting || manualKeyword.trim().length === 0"
          class="btn btn-secondary btn-sm">
          {{ manualSelecting ? 'Selecting...' : 'Select Manual Keyword' }}
        </button>
      </div>
      <p v-if="manualKeywordError" class="mt-2 text-xs text-red-600 dark:text-red-400">
        {{ manualKeywordError }}
      </p>
    </form>

    <!-- Progress -->
    <div v-if="running" class="mb-4">
      <div class="flex items-center gap-2 mb-2">
        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500" />
        <span class="text-sm text-gray-600 dark:text-gray-400">{{ stepMessage }}</span>
      </div>
      <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
        <div class="bg-primary-500 h-1.5 rounded-full transition-all duration-500"
          :style="{ width: progressWidth }" />
      </div>
    </div>

    <!-- Error -->
    <div v-if="research && research.status === 'failed'" class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
      <p class="text-sm text-red-700 dark:text-red-400">
        {{ research.error_message }}
      </p>
    </div>

    <!-- Ranked Candidates -->
    <div v-if="rankedCandidates.length > 0" class="space-y-3">
      <div v-for="candidate in rankedCandidates" :key="candidate.id"
        class="border border-gray-200 dark:border-gray-700 rounded-xl p-4"
        :class="{ 'ring-2 ring-primary-500 dark:ring-primary-400': candidate.selected }">
        <div class="flex items-start justify-between mb-2">
          <div class="flex flex-wrap items-center gap-2">
            <span class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
              :class="rankBadgeClass(candidate.rank)">
              {{ candidate.rank }}
            </span>
            <span class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ candidate.keyword }}</span>
            <span v-if="candidate.recommended_primary"
              class="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
              Recommended Primary
            </span>
          </div>
          <button v-if="!candidate.selected" @click="handleSelect(candidate)"
            class="text-xs text-primary-600 dark:text-primary-400 hover:underline font-medium">
            Select
          </button>
          <span v-else class="text-xs text-primary-600 dark:text-primary-400 font-medium">Selected</span>
        </div>

        <!-- Score bar -->
        <div v-if="candidate.opportunity_score != null" class="flex items-center gap-2 mb-2">
          <div class="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div class="h-2 rounded-full transition-all" :class="scoreBarColor(candidate.opportunity_score)"
              :style="{ width: `${candidate.opportunity_score}%` }" />
          </div>
          <span class="text-xs font-semibold" :class="scoreTextColor(candidate.opportunity_score)">
            {{ candidate.opportunity_score }}
          </span>
        </div>
        <p v-else class="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Manually selected keyword.
        </p>

        <!-- Metric pills -->
        <div class="flex flex-wrap gap-2 mb-2">
          <span v-if="metricsUnavailable(candidate)"
            class="inline-flex items-center px-2 py-0.5 rounded-lg text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
            Keyword data unavailable
          </span>
          <span v-if="showMetric(candidate, 'search_volume')"
            class="inline-flex items-center px-2 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
            Vol: {{ formatVolume(candidate.search_volume) }}{{ candidate.search_volume === 0 ? ' confirmed' : '' }}
          </span>
          <span v-if="showMetric(candidate, 'cpc')"
            class="inline-flex items-center px-2 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
            CPC: ${{ candidate.cpc.toFixed(2) }}
          </span>
          <span v-if="showMetric(candidate, 'competition')"
            class="inline-flex items-center px-2 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
            Comp: {{ (candidate.competition * 100).toFixed(0) }}%
          </span>
          <span v-if="candidate.score_basis"
            class="inline-flex items-center px-2 py-0.5 rounded-lg text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
            {{ scoreBasisLabel(candidate.score_basis) }}
          </span>
          <span v-if="candidate.source"
            class="inline-flex items-center px-2 py-0.5 rounded-lg text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
            {{ candidate.source }}
          </span>
        </div>

        <p v-if="candidate.recommendation_reason" class="text-xs text-gray-500 dark:text-gray-400 mb-2 leading-relaxed">
          {{ candidate.recommendation_reason }}
        </p>

        <!-- SERP features -->
        <div v-if="candidate.serp_data?.serp_features?.length" class="flex flex-wrap gap-1 mb-2">
          <span v-for="feature in candidate.serp_data.serp_features" :key="feature"
            class="inline-flex items-center px-1.5 py-0.5 rounded-lg text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
            {{ formatFeature(feature) }}
          </span>
        </div>

        <!-- Reasoning -->
        <div v-if="candidate.reasoning">
          <button @click="toggleReasoning(candidate.id)" class="text-xs text-gray-500 dark:text-gray-400 hover:underline">
            {{ expandedReasoning[candidate.id] ? 'Hide' : 'Show' }} reasoning
          </button>
          <p v-if="expandedReasoning[candidate.id]" class="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
            {{ candidate.reasoning }}
          </p>
        </div>
      </div>
    </div>

    <!-- Other candidates (unranked) -->
    <div v-if="unrankedCandidates.length > 0" class="mt-4">
      <button @click="showUnranked = !showUnranked"
        class="text-xs text-gray-500 dark:text-gray-400 hover:underline mb-2">
        {{ showUnranked ? 'Hide' : 'Show' }} {{ unrankedCandidates.length }} other variants
      </button>
      <div v-if="showUnranked" class="space-y-1">
        <div v-for="c in unrankedCandidates" :key="c.id"
          class="flex items-center justify-between px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <span>{{ c.keyword }}</span>
          <span v-if="showMetric(c, 'search_volume')">{{ formatVolume(c.search_volume) }}/mo</span>
          <span v-else-if="metricsUnavailable(c)">metrics unavailable</span>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="!research && !running && !loading" class="text-center py-6">
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-2">
        No keyword research yet
      </p>
      <button @click="handleRunResearch" class="btn btn-primary btn-sm">
        Run Keyword Research
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { startResearch, getResearch, selectKeyword } from '~/javascript/api/keyword_research'
import consumer from '~/channels/consumer'

const props = defineProps({
  pageId: { type: String, required: true },
  currentSelectedKeyword: { type: String, default: null }
})

const emit = defineEmits(['updated'])

const loading = ref(true)
const running = ref(false)
const research = ref(null)
const seedKeyword = ref('')
const manualKeyword = ref('')
const manualKeywordError = ref('')
const manualSelecting = ref(false)
const selectedKeywordDraft = ref('')
const selectedKeywordSaving = ref(false)
const editingSelectedKeyword = ref(false)
const showSeedInput = ref(false)
const stepMessage = ref('')
const currentStep = ref('')
const expandedReasoning = ref({})
const showUnranked = ref(false)
const selectedKeyword = ref(props.currentSelectedKeyword)
const seedSuggestions = ref([])
const pageTypeGuidance = ref(null)
const seedValidation = ref(null)
const metricsStatus = ref(null)
let subscription = null
let pollTimer = null

const STEPS = ['generating', 'enriching', 'validating', 'scoring', 'completed']

const progressWidth = computed(() => {
  const idx = STEPS.indexOf(currentStep.value)
  if (idx < 0) return '0%'
  return `${((idx + 1) / STEPS.length) * 100}%`
})

const rankedCandidates = computed(() => {
  if (!research.value?.candidates) return []
  return research.value.candidates.filter((c) => c.rank != null).sort((a, b) => a.rank - b.rank)
})

const unrankedCandidates = computed(() => {
  if (!research.value?.candidates) return []
  return research.value.candidates.filter((c) => c.rank == null)
})

const recommendedSeed = computed(() => seedSuggestions.value.find((suggestion) => suggestion.recommended) || seedSuggestions.value[0])

const seedImpactMessage = computed(() => {
  if (!pageTypeGuidance.value) return ''
  return 'The selected seed drives SERP analysis, competitor selection, keyword strategy, heading/meta recommendations, and content optimization.'
})

const metricsNotice = computed(() => {
  const status = metricsStatus.value || research.value?.metrics_status
  if (!status || status === 'available' || status === 'manual') return ''
  if (status === 'partial') return research.value?.metrics_warning || 'Keyword metrics are only available for some candidates.'
  if (status === 'unavailable') return research.value?.metrics_warning || 'Keyword metrics are unavailable; rankings use page relevance, local intent, and page type.'
  return ''
})

const metricsNoticeClass = computed(() => {
  if ((metricsStatus.value || research.value?.metrics_status) === 'partial') {
    return 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
  }
  return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400'
})

function rankBadgeClass(rank) {
  if (rank === 1) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
  if (rank === 2) return 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
  if (rank === 3) return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
  return 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
}

function scoreBarColor(score) {
  if (score >= 70) return 'bg-primary-500'
  if (score >= 40) return 'bg-amber-500'
  return 'bg-red-500'
}

function scoreTextColor(score) {
  if (score >= 70) return 'text-green-600 dark:text-green-400'
  if (score >= 40) return 'text-amber-600 dark:text-amber-400'
  return 'text-red-600 dark:text-red-400'
}

function formatVolume(vol) {
  if (vol == null) return '?'
  if (vol >= 1000) return `${(vol / 1000).toFixed(1)}k`
  return `${vol}`
}

function formatFeature(feature) {
  const map = {
    local_pack: 'Local Pack',
    knowledge_graph: 'Knowledge Graph',
    featured_snippet: 'Featured Snippet',
    people_also_ask: 'People Also Ask',
    images: 'Images',
    videos: 'Videos'
  }
  return map[feature] || feature.replace(/_/g, ' ')
}

function scoreBasisLabel(basis) {
  const labels = {
    keyword_data_and_relevance: 'Keyword data + relevance',
    keyword_data_and_relevance_and_serp: 'Keyword data + relevance + SERP',
    ai_relevance: 'AI relevance only',
    ai_relevance_and_serp: 'AI relevance + SERP',
    manual_selection: 'Manual selection'
  }
  return labels[basis] || basis.replace(/_/g, ' ')
}

function metricsUnavailable(candidate) {
  return candidate.metrics_status === 'unavailable' || candidate.metrics_status === 'not_checked'
}

function showMetric(candidate, key) {
  return candidate.metrics_status === 'available' && candidate[key] != null
}

function toggleReasoning(id) {
  expandedReasoning.value[id] = !expandedReasoning.value[id]
}

function useSeedSuggestion(suggestion) {
  seedKeyword.value = suggestion.keyword
  seedValidation.value = suggestion.validation || null
  showSeedInput.value = true
}

function beginEditSelectedKeyword() {
  selectedKeywordDraft.value = selectedKeyword.value || ''
  editingSelectedKeyword.value = true
  manualKeywordError.value = ''
}

function hydrateKeywordContext(data) {
  const loadedResearch = data.research || null
  seedSuggestions.value = data.seed_suggestions || loadedResearch?.seed_suggestions || []
  pageTypeGuidance.value = data.page_type_guidance || loadedResearch?.page_type_guidance || null
  seedValidation.value = data.seed_validation || loadedResearch?.seed_validation || null
  metricsStatus.value = data.metrics_status || loadedResearch?.metrics_status || null
  if (!seedKeyword.value && recommendedSeed.value) {
    seedKeyword.value = recommendedSeed.value.keyword
  }
}

async function fetchResearch() {
  try {
    loading.value = true
    const { data } = await getResearch(props.pageId)
    research.value = data.research
    hydrateKeywordContext(data)
    if (research.value?.status === 'running') {
      running.value = true
    }
  } catch (e) {
    console.error('Failed to fetch research:', e) // eslint-disable-line no-console
  } finally {
    loading.value = false
  }
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async() => {
    try {
      const { data } = await getResearch(props.pageId)
      if (data.research && data.research.status !== 'running' && data.research.status !== 'pending') {
        research.value = data.research
        hydrateKeywordContext(data)
        running.value = false
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

async function handleRunResearch() {
  running.value = true
  stepMessage.value = 'Starting research...'
  currentStep.value = ''

  try {
    await startResearch(props.pageId, seedKeyword.value || null)
    showSeedInput.value = false
    startPolling()
  } catch (e) {
    console.error('Research failed:', e) // eslint-disable-line no-console
    running.value = false
  }
}

async function handleSelect(candidate) {
  try {
    const { data } = await selectKeyword(props.pageId, candidate.id)
    selectedKeyword.value = data.keyword
    selectedKeywordDraft.value = data.keyword
    editingSelectedKeyword.value = false
    // Update local state
    if (research.value?.candidates) {
      research.value.candidates.forEach((c) => { c.selected = c.id === candidate.id })
    }
    emit('updated')
  } catch (e) {
    console.error('Selection failed:', e) // eslint-disable-line no-console
  }
}

async function handleSelectedKeywordEdit() {
  const keyword = selectedKeywordDraft.value.trim()
  if (!keyword) return

  selectedKeywordSaving.value = true
  manualKeywordError.value = ''

  try {
    const { data } = await selectKeyword(props.pageId, { keyword })
    selectedKeyword.value = data.keyword
    editingSelectedKeyword.value = false
    await fetchResearch()
    emit('updated')
  } catch (e) {
    manualKeywordError.value = e.response?.data?.message || 'Keyword update failed'
    console.error('Keyword update failed:', e) // eslint-disable-line no-console
  } finally {
    selectedKeywordSaving.value = false
  }
}

async function handleManualKeywordSelect() {
  const keyword = manualKeyword.value.trim()
  if (!keyword) return

  manualSelecting.value = true
  manualKeywordError.value = ''

  try {
    const { data } = await selectKeyword(props.pageId, { keyword })
    selectedKeyword.value = data.keyword
    selectedKeywordDraft.value = data.keyword
    editingSelectedKeyword.value = false
    manualKeyword.value = ''
    await fetchResearch()
    emit('updated')
  } catch (e) {
    manualKeywordError.value = e.response?.data?.message || 'Manual keyword selection failed'
    console.error('Manual keyword selection failed:', e) // eslint-disable-line no-console
  } finally {
    manualSelecting.value = false
  }
}

function subscribeToChannel() {
  subscription = consumer.subscriptions.create(
    { channel: 'KeywordResearchChannel', page_id: props.pageId },
    {
      received(data) {
        currentStep.value = data.step
        stepMessage.value = data.message || ''

        if (data.step === 'completed') {
          running.value = false
          stopPolling()
          fetchResearch()
        } else if (data.step === 'failed') {
          running.value = false
          stopPolling()
          fetchResearch()
        }
      }
    }
  )
}

onMounted(() => {
  fetchResearch()
  subscribeToChannel()
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
